import { createSelector } from '@ngrx/store';
import { Seq } from 'immutable';
import { TripLegStatus } from 'src/graphql/generated/graphql.generated';
import { TripLegRecord } from '../generated/trip-leg-record.generated';
import { VehicleDispositionRecord } from '../generated/vehicle-disposition-record.generated';
import { WaypointRecord } from '../generated/waypoint-record.generated';
import { selectAllTripLegs, selectAllWaypoints } from './entity-store-selectors';
import {
  selectVehicleDispositionByCardNumber,
  selectVehicleDispositionByTripId,
} from './vehicle-disposition-selector';

export function selectTripLegById(tripLegId: string) {
  return createSelector(selectAllTripLegs, (tripLegs) => {
    return tripLegs.valueSeq().find((tl) => tl.id === tripLegId);
  });
}

export function selectNonCancelledTripLegsByTripId(tripId: string) {
  return createSelector(selectAllTripLegs, (tripLegs) => {
    return tripLegs
      .valueSeq()
      .filter((tl) => tl.tripId === tripId && tl.status !== TripLegStatus.Cancelled)
      .sortBy((tl) => tl.sequence);
  });
}

export function selectCurrentOrNextTripLeg(tripId: string) {
  return createSelector(selectNonCancelledTripLegsByTripId(tripId), (tripLegs) => {
    const enRouteLeg = tripLegs.find((leg) => leg.status === TripLegStatus.EnRoute);
    return enRouteLeg != null
      ? enRouteLeg
      : tripLegs.find((tl) => tl.status === TripLegStatus.Planned);
  });
}

export function selectTripLegByTripLegBySequence(tripId: string, sequence: number) {
  return createSelector(selectNonCancelledTripLegsByTripId(tripId), (tripLegs) => {
    const sequenceLeg = tripLegs.find((leg) => leg.sequence === sequence);
    return sequenceLeg != null ? sequenceLeg : null;
  });
}

export function selectEnRouteTripLeg(tripId: string) {
  return createSelector(selectNonCancelledTripLegsByTripId(tripId), (tripLegs) => {
    return tripLegs.find((leg) => leg.status === TripLegStatus.EnRoute);
  });
}

export function selectPlannedTripLeg(tripId: string) {
  return createSelector(selectNonCancelledTripLegsByTripId(tripId), (tripLegs) => {
    return tripLegs.find((tl) => tl.status === TripLegStatus.Planned);
  });
}

export function selectTripLegsWithWaypointsByTripId(tripId: string) {
  return createSelector(
    selectAllWaypoints,
    selectNonCancelledTripLegsByTripId(tripId),
    (waypoints, tripLegs) => {
      return tripLegs.map((leg) => {
        // TODO: check if a leg with waypoints must be returned even if waypoint is undefined.
        const result: LegWithWaypoints = {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          startingWaypoint: waypoints.get(leg.startingWaypointId!)!,
          leg,
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          endingWaypoint: waypoints.get(leg.endingWaypointId!)!,
        };
        return result;
      });
    },
  );
}

export function selectCurrentTripLegByTripId(tripId: string) {
  return createSelector(
    selectNonCancelledTripLegsByTripId(tripId),
    selectVehicleDispositionByTripId(tripId),
    (tripLegs, vehicleDisposition) => {
      return tripLegs.find(
        (tl) => vehicleDisposition != null && tl.id === vehicleDisposition.currentTripLegId,
      );
    },
  );
}

export function selectTripLegAfterTripLegId(tripId: string, currentTripLegId: string) {
  return createSelector(selectNonCancelledTripLegsByTripId(tripId), (tripLegs) => {
    const nextSequence = (tripLegs.find((leg) => leg.id === currentTripLegId)?.sequence ?? NaN) + 1;
    return isNaN(nextSequence) ? undefined : tripLegs.find((tl) => tl.sequence === nextSequence);
  });
}

// Depending on vehicle disposition is not correct in all cases
export function selectCurrentTripLegByCardNumber_DEPRECATED(cardNumber: string) {
  return createSelector(
    selectVehicleDispositionByCardNumber(cardNumber),
    selectAllTripLegs,
    (vehicleDisposition, tripLegs) => {
      return tripLegs.find(
        (leg) => vehicleDisposition != null && leg.id === vehicleDisposition.currentTripLegId,
      );
    },
  );
}

// Depending on vehicle disposition is not correct in all cases
export function selectCurrentOrNextTripLegByCardNumber_DEPRECATED(cardNumber: string) {
  return createSelector(
    selectVehicleDispositionByCardNumber(cardNumber),
    selectAllTripLegs,
    (vehicleDisposition, tripLegs) => {
      if (vehicleDisposition != null) {
        return findTripLeg(tripLegs.valueSeq(), vehicleDisposition);
      }
      return null;
    },
  );
}

// Depending on vehicle disposition is not correct in all cases
export function selectCurrentOrNextTripLegByTripId_DEPRECATED(tripId: string) {
  return createSelector(
    selectVehicleDispositionByTripId(tripId),
    selectAllTripLegs,
    (vehicleDisposition, tripLegs) => {
      if (vehicleDisposition != null) {
        return findTripLeg(tripLegs.valueSeq(), vehicleDisposition);
      }
      return null;
    },
  );
}

function findTripLeg(
  tripLegs: Seq.Indexed<TripLegRecord>,
  vehicleDisposition: VehicleDispositionRecord,
) {
  return tripLegs.find(
    (leg) =>
      (vehicleDisposition.currentTripLegId != null &&
        leg.id === vehicleDisposition.currentTripLegId) ||
      (vehicleDisposition.currentWaypointId != null &&
        leg.startingWaypointId === vehicleDisposition.currentWaypointId),
  );
}

export interface LegWithWaypoints {
  startingWaypoint: WaypointRecord;
  leg: TripLegRecord;
  endingWaypoint: WaypointRecord;
}
